import { isNativeApp } from './platform';

/**
 * Small key/value store used for client-side audio progress, ratings, and
 * favourite/hidden flags (see audioProgressUtils). These values are NEVER sent
 * to the server as HTTP cookies — the API authenticates with a Bearer token —
 * so the backing store is purely an implementation detail.
 *
 * On the web we use real cookies (unchanged). In the native iOS shell the
 * WebView runs at `capacitor://localhost`, where WKWebView silently drops
 * `document.cookie` (writes vanish, reads return ""), so nothing persisted and
 * server->local hydration had nothing to write to — cross-device progress never
 * showed up. There we transparently back the same API with localStorage, which
 * works reliably on the custom scheme (auth tokens already rely on it).
 */

// localStorage namespace so these entries are distinguishable from other keys
// (e.g. auth tokens) and can be enumerated for the progress-sync read path.
const NATIVE_STORE_PREFIX = 'cookieStore:';

const useNativeStore = (): boolean => {
  try {
    return isNativeApp();
  } catch {
    return false;
  }
};

/**
 * Set a cookie with the given name, value, and expiration
 * @param name - Cookie name
 * @param value - Cookie value
 * @param days - Number of days until expiration (default: 30); ignored on native
 */
export const setCookie = (name: string, value: string, days: number = 30): void => {
  if (useNativeStore()) {
    try {
      localStorage.setItem(NATIVE_STORE_PREFIX + name, value);
    } catch {
      // Storage unavailable/full — best effort, mirrors cookies being dropped.
    }
    return;
  }
  const expires = new Date();
  expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
};

/**
 * Get a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or null if not found
 */
export const getCookie = (name: string): string | null => {
  if (useNativeStore()) {
    try {
      return localStorage.getItem(NATIVE_STORE_PREFIX + name);
    } catch {
      return null;
    }
  }
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

/**
 * Delete an entry by name. Takes the bare name, as returned by readAllEntries.
 * On the web the cookie is expired at `path=/` to match how setCookie wrote it;
 * a mismatched path would silently leave the value in place.
 */
export const removeCookie = (name: string): void => {
  if (useNativeStore()) {
    try {
      localStorage.removeItem(NATIVE_STORE_PREFIX + name);
    } catch {
      // Storage unavailable — nothing to remove.
    }
    return;
  }
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
};

/**
 * Enumerate every entry in the store as {key, value}. Used by the progress-sync
 * read path (collecting local progress, listing hydrated entries). Returns the
 * bare names (without the native prefix) so callers see the same keys on both
 * platforms.
 */
export const readAllEntries = (): Array<{ key: string; value: string }> => {
  if (useNativeStore()) {
    const entries: Array<{ key: string; value: string }> = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const storageKey = localStorage.key(i);
        if (!storageKey || !storageKey.startsWith(NATIVE_STORE_PREFIX)) {
          continue;
        }
        const value = localStorage.getItem(storageKey);
        if (value !== null) {
          entries.push({ key: storageKey.slice(NATIVE_STORE_PREFIX.length), value });
        }
      }
    } catch {
      // Ignore — treat as empty store.
    }
    return entries;
  }

  if (typeof document === 'undefined' || !document.cookie) {
    return [];
  }
  return document.cookie.split(';').flatMap((cookie) => {
    const trimmed = cookie.trim();
    const separatorIndex = trimmed.indexOf('=');
    if (separatorIndex === -1) {
      return [];
    }
    return [{
      key: trimmed.slice(0, separatorIndex),
      value: trimmed.slice(separatorIndex + 1),
    }];
  });
};
